import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { ROLE_LABELS } from '../utils/format'
import LoginModal from './LoginModal'
import logoIcon from '../assets/logo-icon.png'

// ── Main navigation: 5 core features ──
const mainNavItems = [
  { path: '/', label: '工作台', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { path: '/assets', label: '品牌记忆库', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
  { path: '/search', label: '智能搜索', icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' },
  { path: '/database', label: '数据库', icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4' },
  { path: '/knowledge-graph', label: '知识图谱', icon: 'M12 3a2 2 0 100 4 2 2 0 000-4zm-7 12a2 2 0 100 4 2 2 0 000-4zm14 0a2 2 0 100 4 2 2 0 000-4zM12 7v4m0 0l-5.5 5.5M12 11l5.5 5.5' },
  // 路由本身要求 edit；导航项必须跟着权限走，否则 viewer 看到一个点不开的入口
  { path: '/review-queue', label: '待审阅', requiredPermission: 'edit', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
  { path: '/insights', label: '品牌洞察', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
]

// ── Settings navigation ──
const settingsNavItems = [
  { path: '/settings', label: '设置', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
]

// ── Tools navigation ──
const toolsNavItems = [
  { path: '/shares', label: '分享管理', icon: 'M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z' },
  { path: '/vault', label: '保险库', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' },
]

function NavIcon({ d, className = '' }) {
  return (
    <svg className={`w-[18px] h-[18px] flex-shrink-0 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d={d} />
    </svg>
  )
}

function GradientOrb() {
  return (
    <span className="relative flex items-center justify-center w-7 h-7">
      <span className="absolute inset-0 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 opacity-15 blur-[6px]" />
      <span className="relative w-[9px] h-[9px] rounded-full bg-gradient-to-br from-brand-500 to-brand-700" />
    </span>
  )
}

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, isAuthenticated, showLogin, logout, openLogin, closeLogin, hasPermission } = useAuth()
  const location = useLocation()

  const isSettingsPath = location.pathname.startsWith('/settings')

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--color-bg)] font-sans">

      {/* ── Mobile backdrop ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-40 w-[220px] bg-white border-r border-[var(--color-line-light)]
          flex flex-col
          transform transition-transform duration-200 ease-out
          lg:relative lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo area */}
        <div className="flex items-center gap-2.5 px-4 h-[52px] flex-shrink-0">
          <img src={logoIcon} alt="" className="w-7 h-7 flex-shrink-0" />
          <div className="flex flex-col leading-none">
            <span className="text-[13px] font-semibold tracking-tight text-[var(--color-ink-primary)]">
              全景OmniTuring
            </span>
            <span className="text-[10px] text-[var(--color-ink-tertiary)] mt-0.5">
              品牌数字资产管理
            </span>
          </div>
        </div>

        {/* Navigation — scrollable area */}
        <nav className="flex-1 overflow-y-auto px-2.5 pt-4 pb-2">

          {/* Section: 功能 */}
          <div className="px-3 mb-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--color-ink-tertiary)]">
            功能
          </div>

          <div className="space-y-px">
            {mainNavItems.filter((item) => !item.requiredPermission || hasPermission(item.requiredPermission)).map((item) => {
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium
                    transition-colors duration-150
                    ${isActive
                      ? 'bg-[var(--color-brand-50)] text-[var(--color-brand-600)]'
                      : 'text-[var(--color-ink-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-ink-primary)]'
                    }
                  `}
                >
                  {/* Active left accent bar */}
                  {isActive && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full bg-[var(--color-brand-600)]" />
                  )}
                  <NavIcon d={item.icon} />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </div>

          {/* Divider + Settings section (manage_settings only) */}
          {hasPermission('manage_settings') && (
            <>
              <div className="my-3 mx-3 border-t border-[var(--color-line-light)]" />

              {/* Section: 管理 */}
              <div className="px-3 mb-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--color-ink-tertiary)]">
                管理
              </div>

              <div className="space-y-px">
                {settingsNavItems.map((item) => {
                  const isActive = isSettingsPath
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`
                        relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium
                        transition-colors duration-150
                        ${isActive
                          ? 'bg-[var(--color-brand-50)] text-[var(--color-brand-600)]'
                          : 'text-[var(--color-ink-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-ink-primary)]'
                        }
                      `}
                    >
                      {isActive && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full bg-[var(--color-brand-600)]" />
                      )}
                      <NavIcon d={item.icon} />
                      <span>{item.label}</span>
                    </Link>
                  )
                })}
              </div>
            </>
          )}

          {/* Divider + Tools section */}
          <div className="my-3 mx-3 border-t border-[var(--color-line-light)]" />

          {/* Section: 工具 */}
          <div className="px-3 mb-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--color-ink-tertiary)]">
            工具
          </div>

          <div className="space-y-px">
            {toolsNavItems.map((item) => {
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium
                    transition-colors duration-150
                    ${isActive
                      ? 'bg-[var(--color-brand-50)] text-[var(--color-brand-600)]'
                      : 'text-[var(--color-ink-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-ink-primary)]'
                    }
                  `}
                >
                  {isActive && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full bg-[var(--color-brand-600)]" />
                  )}
                  <NavIcon d={item.icon} />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </div>
        </nav>

        {/* ── User area (bottom) ── */}
        {isAuthenticated && user && (
          <div className="flex-shrink-0 border-t border-[var(--color-line-light)] px-3 py-3">
            <div className="flex items-center gap-2.5">
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full bg-[var(--color-brand-50)] text-[var(--color-brand-600)] flex items-center justify-center text-[12px] font-semibold flex-shrink-0 select-none">
                {(user.name || user.email || 'U').charAt(0).toUpperCase()}
              </div>

              {/* Name + role */}
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[var(--color-ink-primary)] truncate leading-tight">
                  {user.name || user.email}
                </div>
                <div className="text-[11px] text-[var(--color-ink-tertiary)] leading-tight mt-px">
                  {ROLE_LABELS[user.role] || user.role}
                </div>
              </div>

              {/* Logout button */}
              <button
                onClick={logout}
                className="p-1.5 rounded-md text-[var(--color-ink-tertiary)] hover:text-[var(--color-ink-secondary)] hover:bg-[var(--color-bg-hover)] transition-colors"
                title="退出登录"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </aside>

      {/* ── Main content area ── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Header bar */}
        <header className="flex items-center h-[52px] px-4 bg-white/80 backdrop-blur-md border-b border-[var(--color-line-light)] sticky top-0 z-20 flex-shrink-0">
          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-1.5 -ml-0.5 rounded-lg text-[var(--color-ink-secondary)] hover:bg-[var(--color-bg-hover)] transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <div className="flex-1" />

          {/* Login button (only when not authenticated) */}
          {!isAuthenticated && (
            <button
              onClick={openLogin}
              className="px-3.5 py-1.5 text-[13px] font-medium text-white bg-brand-700 rounded-lg hover:bg-brand-800 transition-colors"
            >
              登录
            </button>
          )}
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>

        {/* Footer — ICP filing */}
        <footer className="flex-shrink-0 border-t border-[var(--color-line-light)] bg-white/60 py-2 text-center">
          {/* Link 不能嵌在 <a> 里（HTML 非法且点击行为会串） */}
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <a
              href="https://beian.miit.gov.cn"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-[var(--color-ink-tertiary)] hover:text-[var(--color-ink-secondary)] transition-colors"
            >
              蜀ICP备2026035347号-1
            </a>
            <a
              href="https://beian.mps.gov.cn/#/query/webSearch?code=51010602003559"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-[var(--color-ink-tertiary)] hover:text-[var(--color-ink-secondary)] transition-colors"
            >
              <img src="/gongan.png" alt="公安备案" width="20" height="20" className="inline-block" />
              川公网安备51010602003559号
            </a>
            <Link to="/privacy" className="text-[11px] text-[var(--color-ink-tertiary)] hover:text-[var(--color-ink-secondary)] transition-colors">隐私政策</Link>
            <Link to="/terms" className="text-[11px] text-[var(--color-ink-tertiary)] hover:text-[var(--color-ink-secondary)] transition-colors">服务协议</Link>
          </div>
        </footer>
      </div>

      {/* Login Modal */}
      {showLogin && <LoginModal onClose={closeLogin} />}
    </div>
  )
}
